import { useMutation, useQuery } from 'apollo-3';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';

import {
  guidedPracticeGradeExamPaper,
  guidedPracticeSubmitExamPaperAnswer,
} from 'GraphQL/mutation/GuidedPractice.mutation';
import { getGuidedPracticeExamPaper, getGuidedPractices } from 'GraphQL/query/GuidedPractice.query';
import { useDisclosure } from 'Util/Hooks';

import {
  processMultipleChoiceSubmissionResult,
  processMultipleSelectSubmissionResult,
  selectMultipleChoiceAnswer,
  toggleMultipleSelectAnswer,
  submitMultipleChoiceAnswer,
  submitMultipleSelectAnswer,
} from '../../Util/ExamRoom/examAnswerInteractions';
import { EXAM_PAPER_ITEM_TYPE } from '../../Components/Quiz/constant';

import { EXAM_PAPER_TYPE, mapGuidedPracticeExamPaperItemsToExamItems } from './mapper';

const ExamRoomContext = React.createContext();

const calculateCurrentQuestionNumber = (items, index) => {
  return items.filter((item, i) => item.type === EXAM_PAPER_TYPE.QUESTION && i <= index).length;
};

const ExamRoomProvider = (props) => {
  const { children, sessionId, redirectPath } = props;
  const [error, setError] = React.useState(null);
  const [currentExamItemIndex, setCurrentExamItemIndex] = React.useState(0);
  const [questionAnswers, setQuestionAnswers] = React.useState({});
  const [correctQuestionSolution, setCorrectQuestionSolution] = React.useState({});
  const [selectedChoices, setSelectedChoices] = React.useState([]);
  const [openSolutionDetailsModal, onOpenSolutionDetailsModal, onCloseSolutionDetailsModal] =
    useDisclosure();
  const [currentQuestionNumber, setCurrentQuestionNumber] = React.useState(1);
  const history = useHistory();
  const [errorSubmitExamPaper, setErrorSubmitExamPaper] = React.useState(false);
  const [errorGradeExamPaper, setErrorGradeExamPaper] = React.useState(false);

  const {
    data,
    loading,
    error: examPaperError,
  } = useQuery(getGuidedPracticeExamPaper, {
    variables: { examSessionId: sessionId },
  });

  const [submitExamPaper, { loading: loadingSubmitExamPaper }] = useMutation(
    guidedPracticeSubmitExamPaperAnswer,
    {
      refetchQueries: [
        { query: getGuidedPracticeExamPaper, variables: { examSessionId: sessionId } },
      ],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        if (data?.guidedPracticeSubmitExamPaperAnswer?.success) {
          const question = examItems[currentExamItemIndex];
          const answer = questionAnswers[question.id];

          let answerEvaluationResult;
          switch (question.questionType) {
            case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE:
              answerEvaluationResult = submitMultipleChoiceAnswer(
                question,
                answer,
                selectedChoices
              );
              break;
            case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
              answerEvaluationResult = submitMultipleSelectAnswer(
                question,
                answer,
                selectedChoices
              );
              break;
            default:
              console.error(`Unsupported question type: ${question.questionType}`);
              return;
          }

          setCorrectQuestionSolution({
            ...answerEvaluationResult,
            solution: question.solution,
          });
          setErrorSubmitExamPaper(false);
          onOpenSolutionDetailsModal();
        } else {
          setErrorSubmitExamPaper(true);
          console.error(
            'Error submitting exam paper:',
            data?.guidedPracticeSubmitExamPaperAnswer?.message
          );
        }
      },
      onError: (error) => {
        setErrorSubmitExamPaper(true);
        console.error('Error submitting exam paper:', error);
      },
    }
  );

  const [gradeExamPaper, { loading: loadingGradeExamPaper }] = useMutation(
    guidedPracticeGradeExamPaper,
    {
      refetchQueries: [
        { query: getGuidedPracticeExamPaper, variables: { examSessionId: sessionId } },
        { query: getGuidedPractices },
      ],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        if (data?.guidedPracticeGradeExamPaper?.success) {
          setErrorGradeExamPaper(false);
          history.replace(redirectPath);
        } else {
          setErrorGradeExamPaper(true);
          console.error('Error grading exam paper:', data?.guidedPracticeGradeExamPaper?.message);
        }
      },
      onError: (error) => {
        setErrorGradeExamPaper(true);
        console.error('Error grading exam paper:', error);
      },
    }
  );

  const examItems = React.useMemo(() => {
    try {
      if (loading) return [];
      return (
        mapGuidedPracticeExamPaperItemsToExamItems(
          data?.guidedPracticeExamPaper?.examPaperItemViews
        ) ?? []
      ).sort((a, b) => a.order - b.order);
    } catch (error) {
      setError(error);
      console.error('mapGuidedPracticeExamPaperItemsToExamItems error:', error);
      return [];
    }
  }, [data?.guidedPracticeExamPaper?.examPaperItemViews, loading]);

  const totalQuestionAmount = useMemo(
    () => examItems.filter((item) => item.type === EXAM_PAPER_TYPE.QUESTION).length,
    [examItems]
  );

  // NOTED: This useEffect is used to set the current exam item index to the last submitted question
  useEffect(() => {
    if (examItems.length === 0) return;

    let lastSubmittedQuestionIndex = -1;
    for (let i = examItems.length - 1; i >= 0; i--) {
      const item = examItems[i];
      if (item.type === EXAM_PAPER_TYPE.QUESTION && item.answer?.value != null) {
        lastSubmittedQuestionIndex = i;
        break;
      }
    }

    if (lastSubmittedQuestionIndex > -1) {
      // NOTED: Count done questions
      // ใช้เฉพาะ QUESTION type เท่านั้น โดยนับถึง index ปัจจุบัน
      setCurrentQuestionNumber(
        calculateCurrentQuestionNumber(examItems, lastSubmittedQuestionIndex)
      );

      setCurrentExamItemIndex(lastSubmittedQuestionIndex);
      const lastExamItem = examItems[lastSubmittedQuestionIndex];

      let submissionResult = {};
      switch (lastExamItem.questionType) {
        case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE:
          submissionResult = processMultipleChoiceSubmissionResult(lastExamItem);
          break;
        case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
          submissionResult = processMultipleSelectSubmissionResult(lastExamItem);
          break;
        default:
          console.error(`Unsupported question type: ${lastExamItem.questionType}`);
          return;
      }

      setSelectedChoices(submissionResult.selectedIndices || []);
      setCorrectQuestionSolution({
        selectedIndices: submissionResult.selectedIndices || [],
        correctAnswerIndices: submissionResult.correctAnswerIndices || [],
        isCorrect: submissionResult.isCorrect,
        solution: lastExamItem.solution,
      });
    }
  }, [examItems]);

  const currentExamItem = useMemo(
    () => examItems[currentExamItemIndex],
    [examItems, currentExamItemIndex]
  );

  const handleSelectAnswer = (examPaperItemId, answerIndex) => {
    let answerSelectionResult;

    switch (currentExamItem.questionType) {
      case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE:
        answerSelectionResult = selectMultipleChoiceAnswer(currentExamItem, answerIndex);
        break;
      case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
        answerSelectionResult = toggleMultipleSelectAnswer(
          currentExamItem,
          answerIndex,
          selectedChoices
        );
        break;
      default:
        console.error(`Unsupported question type: ${currentExamItem.questionType}`);
        return;
    }

    setSelectedChoices(answerSelectionResult.newSelections);
    setQuestionAnswers((prev) => ({
      ...prev,
      [examPaperItemId]: answerSelectionResult.selectedValues,
    }));
  };

  const handleSubmitExamPaper = async () => {
    if (currentExamItem.type !== EXAM_PAPER_TYPE.QUESTION) {
      throw new Error(`This examPaperItemId: ${currentExamItem.id} is not a QUESTION type`);
    } else {
      const rawAnswer = questionAnswers[currentExamItem.id];
      let answerValue;

      switch (currentExamItem.questionType) {
        case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE:
          answerValue = rawAnswer.toString();
          break;
        case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
          answerValue = rawAnswer
            .split(',')
            .map((v) => v.trim())
            .join(',');
          break;
        default:
          console.error(`Unsupported question type: ${currentExamItem.questionType}`);
          return;
      }

      await submitExamPaper({
        variables: {
          examSessionId: sessionId,
          examPaperItemId: currentExamItem.id,
          answer: { value: answerValue },
        },
      });
    }
  };

  const handleGradeExamPaper = async () => {
    await gradeExamPaper({
      variables: {
        examSessionId: sessionId,
      },
    });
  };

  const handleNextExamItem = () => {
    onCloseSolutionDetailsModal();
    const nextIndex = currentExamItemIndex + 1;

    if (nextIndex >= examItems.length) {
      handleGradeExamPaper();
      return;
    }

    setSelectedChoices([]);
    setCurrentExamItemIndex(nextIndex);
    setCorrectQuestionSolution({});

    const nextExamItem = examItems[nextIndex];
    // NOTED: Update current question number only if next item is a QUESTION
    if (nextExamItem.type === EXAM_PAPER_TYPE.QUESTION) {
      setCurrentQuestionNumber(calculateCurrentQuestionNumber(examItems, nextIndex));
    }
  };

  const handleSubmitAnswer = () => {
    const question = examItems[currentExamItemIndex];
    if (question.type === EXAM_PAPER_TYPE.QUESTION) {
      handleSubmitExamPaper();
    } else {
      throw new Error(`This examPaperItemId: ${question.id} is not a QUESTION type`);
    }
  };

  return (
    <ExamRoomContext.Provider
      value={{
        loading,
        error: error ?? examPaperError,
        examItems,
        currentExamItem,
        handleSelectAnswer,
        handleNextExamItem,
        handleSubmitAnswer,
        openSolutionDetailsModal,
        correctQuestionSolution,
        onCloseSolutionDetailsModal,
        onOpenSolutionDetailsModal,
        selectedChoices,
        totalQuestionAmount,
        currentQuestionNumber,
        errorGradeExamPaper,
        loadingGradeExamPaper,
        setErrorGradeExamPaper,
        loadingSubmitExamPaper,
        errorSubmitExamPaper,
        setErrorSubmitExamPaper,
      }}
    >
      {children}
    </ExamRoomContext.Provider>
  );
};

ExamRoomProvider.propTypes = {
  children: PropTypes.node,
  sessionId: PropTypes.string.isRequired,
  redirectPath: PropTypes.string.isRequired,
};

export const useExamRoomContext = () => {
  const context = React.useContext(ExamRoomContext);

  if (context === undefined) {
    throw new Error('useExamRoomContext must be used within a ExamRoomProvider');
  }
  return context;
};

export default ExamRoomProvider;
